import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { AlertCircle, Activity, CheckCircle } from 'lucide-react';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const justReset = params.get('reset') === 'success';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    // Normalize email input: trim accidental whitespace and lowercase.
    // Password is intentionally left untouched (no trim) — preserves any
    // significant whitespace the user actually typed.
    const cleanEmail = (email || '').trim().toLowerCase();

    try {
      const userData = await login(cleanEmail, password);
      const route = userData?.role === 'ATHLETE' ? '/athlete/wellness' : '/staff/roster';
      navigate(route, { replace: true });
    } catch (err) {
      if (err.response?.status === 401) {
        setError(t('invalidCredentials'));
      } else if (err.response?.status === 423) {
        setError(t('accountLocked'));
      } else {
        setError(t('error'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex flex-col items-center justify-center p-4">
      {/* Background */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-zinc-900 via-zinc-950 to-zinc-950" />
      
      {/* Language toggle */}
      <button
        onClick={toggleLanguage}
        className="absolute top-4 right-4 px-3 py-1.5 text-xs font-semibold tracking-wider uppercase bg-zinc-900 border border-zinc-800 rounded-sm text-zinc-400 hover:text-zinc-100 hover:border-zinc-700 transition-colors z-10"
        data-testid="language-toggle"
      >
        {language}
      </button>

      {/* Brand — above the card */}
      <div className="relative mb-8">
        <img src="/logo/fb-logo-dark.png" alt="FB Performance" className="h-14 sm:h-16 w-auto object-contain mx-auto" data-testid="login-logo" />
      </div>

      <Card className="relative w-full max-w-md bg-zinc-900/80 border-zinc-800 backdrop-blur-sm">
        <CardHeader className="pb-2 pt-6">
          <CardDescription className="text-zinc-500 text-center">
            {t('signInDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          <form onSubmit={handleSubmit} className="space-y-4" autoComplete="on">
            {justReset && !error && (
              <div className="flex items-center gap-2 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-sm text-emerald-300 text-sm" data-testid="reset-success-banner">
                <CheckCircle className="w-4 h-4 flex-shrink-0" />
                <span>Your new password is now active. Sign in below.</span>
              </div>
            )}
            {error && (
              <div className="flex items-center gap-2 p-3 bg-rose-500/10 border border-rose-500/20 rounded-sm text-rose-400 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-semibold tracking-wider uppercase text-zinc-400">
                {t('email')}
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@example.com"
                required
                autoComplete="username"
                spellCheck={false}
                className="bg-zinc-950 border-zinc-800 focus:border-amber-500 focus:ring-amber-500/20 rounded-sm placeholder:text-zinc-600"
                data-testid="login-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-semibold tracking-wider uppercase text-zinc-400">
                {t('password')}
              </Label>
              <Input
                id="password"
                name="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                autoComplete="current-password"
                className="bg-zinc-950 border-zinc-800 focus:border-amber-500 focus:ring-amber-500/20 rounded-sm placeholder:text-zinc-600"
                data-testid="login-password"
              />
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full bg-amber-500 text-zinc-950 hover:bg-amber-400 font-bold uppercase tracking-wide rounded-sm h-11 mt-2"
              data-testid="login-submit"
            >
              {isLoading ? t('loading') : t('signIn')}
            </Button>
          </form>

          <div className="text-center mt-3">
            <a href="/reset-password" className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors" data-testid="forgot-password-link">
              Forgot password?
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;
